import React, { useState, useEffect } from 'react';
import { useAuth, getAuthHeaders } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { 
  Archive, ArrowLeft, Package, RefreshCw, Check,
  AlertTriangle, Loader2, ChevronRight, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { resolveImageUrl } from '../utils/imageUrl';

import { BACKEND_URL } from '../config/api';

const StashedAwayPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [items, setItems] = useState([]);
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [itemsRes, statusRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/billing/stashed-away`, {
          credentials: 'include',
          headers: getAuthHeaders()
        }),
        fetch(`${BACKEND_URL}/api/billing/status`, {
          credentials: 'include',
          headers: getAuthHeaders()
        })
      ]);

      if (itemsRes.ok) {
        const data = await itemsRes.clone().json();
        setItems(data.items || []);
      }
      
      if (statusRes.ok) {
        const data = await statusRes.clone().json();
        setBillingStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch stashed away items:', error);
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (itemId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map(i => i.item_id)));
    }
  };

  const handleRestore = async () => {
    if (selectedItems.size === 0) {
      toast.error('Select items to restore');
      return;
    }

    const availableSlots = (billingStatus?.total_capacity || 20) - (billingStatus?.stash_item_count || 0);
    
    if (availableSlots <= 0) {
      toast.error('No room in main stash. Upgrade your plan or stash away other items first.');
      return;
    }

    setRestoring(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/billing/stash-away`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          item_ids: Array.from(selectedItems),
          action: 'restore'
        })
      });

      const data = await response.clone().json();

      if (response.ok) {
        toast.success(data.message || `Restored ${data.items_restored} items!`);
        setSelectedItems(new Set());
        await fetchData();
        await refreshUser();
      } else {
        toast.error(data.detail || 'Failed to restore items');
      }
    } catch (error) {
      toast.error('Failed to restore items');
    } finally {
      setRestoring(false);
    }
  };

  const availableSlots = billingStatus 
    ? Math.max(0, billingStatus.total_capacity - billingStatus.stash_item_count)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-cream">
        <Navigation />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-sage" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/stash')}
            className="p-2 hover:bg-beige rounded-lg transition-colors"
            data-testid="back-to-stash-btn"
          >
            <ArrowLeft className="h-5 w-5 text-gray-soft" />
          </button>
          <div>
            <h1 className="text-2xl font-light text-charcoal flex items-center gap-2">
              <Archive className="h-6 w-6 text-amber-600" />
              Stashed Away
            </h1>
            <p className="text-gray-soft text-sm">
              Items stored over your plan limit
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="mb-6 border-amber-200 bg-amber-50/50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">What is Stashed Away?</p>
                <p className="text-amber-700">
                  These items are stored safely but don't participate in search or AI suggestions. 
                  They're here because they exceed your current plan limit. You can restore items 
                  to your main stash when you have room, or upgrade your plan for more capacity.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Capacity Status */}
        <Card className="mb-6">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-soft">Main Stash Capacity</div>
                <div className="text-lg font-medium text-charcoal">
                  {billingStatus?.stash_item_count || 0} / {billingStatus?.total_capacity || 20}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-soft">Available Slots</div>
                <div className={`text-lg font-medium ${availableSlots > 0 ? 'text-sage' : 'text-red-500'}`}>
                  {availableSlots}
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/billing#pricing-calculator')}
                data-testid="upgrade-plan-btn"
              >
                Upgrade Plan
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Action Bar */}
        {items.length > 0 && (
          <div className="flex items-center justify-between mb-4 p-3 bg-white rounded-lg border border-beige">
            <div className="flex items-center gap-3">
              <Checkbox 
                checked={selectedItems.size === items.length && items.length > 0}
                onCheckedChange={selectAll}
                id="select-all"
                data-testid="select-all-checkbox"
              />
              <label htmlFor="select-all" className="text-sm text-gray-soft cursor-pointer">
                Select All ({items.length})
              </label>
            </div>
            
            <div className="flex items-center gap-2">
              {selectedItems.size > 0 && (
                <span className="text-sm text-gray-soft">
                  {selectedItems.size} selected
                </span>
              )}
              <Button
                onClick={handleRestore}
                disabled={selectedItems.size === 0 || restoring || availableSlots === 0}
                className="bg-sage hover:bg-sage-dark"
                size="sm"
                data-testid="restore-selected-btn"
              >
                {restoring ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                Restore {selectedItems.size > 0 ? `(${Math.min(selectedItems.size, availableSlots)})` : ''}
              </Button>
            </div>
          </div>
        )}

        {/* Items List */}
        {items.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Archive className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-charcoal mb-2">No Stashed Away Items</h3>
              <p className="text-gray-soft mb-4">
                All your items are in your main stash. Great job staying organized!
              </p>
              <Button 
                variant="outline" 
                onClick={() => navigate('/stash')}
                data-testid="go-to-stash-btn"
              >
                Go to Stash
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.item_id}
                className={`flex items-center gap-4 p-4 bg-white rounded-lg border transition-colors ${
                  selectedItems.has(item.item_id) 
                    ? 'border-sage bg-sage-light/10' 
                    : 'border-beige hover:border-sage/50'
                }`}
                data-testid={`stashed-item-${item.item_id}`}
              >
                <Checkbox
                  checked={selectedItems.has(item.item_id)}
                  onCheckedChange={() => toggleSelect(item.item_id)}
                  data-testid={`select-${item.item_id}`}
                />
                
                {/* Image */}
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-beige flex-shrink-0">
                  {item.image_url ? (
                    <img 
                      src={resolveImageUrl(item.image_url)}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="h-6 w-6 text-gray-300" />
                    </div>
                  )}
                </div>
                
                {/* Details */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-charcoal truncate">{item.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-gray-soft">
                    <span>{item.category?.replace('_', ' ')}</span>
                    {item.brand && (
                      <>
                        <span>•</span>
                        <span>{item.brand}</span>
                      </>
                    )}
                  </div>
                  {item.stashed_away_at && (
                    <div className="text-xs text-gray-400 mt-1">
                      Stashed: {new Date(item.stashed_away_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
                
                {/* Colors */}
                {item.colors && item.colors.length > 0 && (
                  <div className="flex gap-1 flex-shrink-0 overflow-hidden max-w-[80px]">
                    {item.colors.map((color, idx) => (
                      <div
                        key={idx}
                        className="w-4 h-4 rounded-full border border-gray-200 flex-shrink-0"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Help Text */}
        {items.length > 0 && availableSlots === 0 && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Main stash is full</p>
                <p className="text-amber-700">
                  To restore items, you'll need to either upgrade your plan or move some items 
                  from your main stash to Stashed Away.
                </p>
                <Button 
                  variant="link" 
                  className="px-0 h-auto text-amber-700 hover:text-amber-800"
                  onClick={() => navigate('/billing#pricing-calculator')}
                >
                  View upgrade options <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StashedAwayPage;
